import { useMsal } from "@azure/msal-react";
import {
    Stack,
    IStackProps,
    useTheme,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { createNonStandard, createNonStandardTemplate, emitCustomEvent, fadeIn, friendlyValue } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, addUpdateNonStandardTemplates } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, timestamp } from "global/Utility/TSFunctions";
import {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IFlexibleFieldValue as FAM_IFlexibleFieldValue,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IObjectWithNameId as FAM_IObjectWithNameId,
    IRef as FAM_IRef,
    IStopLoss as FAM_IStopLoss,
    ITemplateRelationship as FAM_ITemplateRelationship,
    ITemplateSectionRelationship as FAM_ITemplateSectionRelationship,
    TGuid as FAM_TGuid
} from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import { EControlType } from "../../components/ControlWrapper";
import { setProcessing } from "../../global/Reducer/statusSlice";
import saveAs from "file-saver";
import { ESaveType, SaveAsync } from "global/Application/dataActions";
import { EntityType } from "global/Utility/EntityHelpers";
import { $att_$non_standard_$bcbs_benefitdefinition_id, $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id, $att_$non_standard_template_$bcbs_nst_benefitdefinition_id, $att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id } from "model/ConfigAttributeConstants";

interface IConfirm {
}


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    nonStandardTemplate: FAM_INonStandardTemplate;

    onDismiss: () => void;
}

const enum EView {
    View_CreateTemplate,
    View_LinkTemplate,
    View_Source
};

enum ENonstandardScope {
    GroupConfiguration,
    Benefit
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM_IObjectWithNameId {
    entityType: EntityType;
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    stopLoss?: FAM_IStopLoss;
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IAccountsReceivable | FAM_IFeesAndPremiums | FAM_IGroupConfigurationData;
    type: ENonstandardScope;
}

interface INonstandardTemplateScope {
    nonStandardTemplate: FAM_INonStandardTemplate;
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateSearchResult?: INonstandardTemplateScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateCategoryLookup = (props: IProps) => {
    // current view state/stages
    const [nonStandardTemplate, setNonStandardTemplate] = useState<FAM_INonStandardTemplate>(props.nonStandardTemplate);

    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: props.state.data.snapshot,
        json: props.state.data.json,
    });
    const state = props.state;

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);


    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.isOpen) {
            setSourceContext({
                snapshot: props.state.data.snapshot,
                json: props.state.data.json,
                scopeSearchResult: null,
            });
            setNonStandardTemplate(structuredClone(props.nonStandardTemplate));
        }
    }, [props.isOpen]);

    /**
   * Search for all available non-standards inside a snapshot and output list of scopes
   * Group Configuration, Benefit 1, Benefit 2, ...
   * @param value 
   * @returns 
   */
    const onSearchScopes: TSearchPredicateAsync<IScopeSearchResult> = async (value) => {

        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        // Use props.nonStandardTemplate directly here because local state may not yet be updated
        // when this function is called on initial open (both useEffects fire in the same render cycle).
        const searchTemplate = props.nonStandardTemplate;

        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<IScopeSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));

            if (searchTemplate?.linkedCategoryName === "Group Configuration") {
                if ((json?.nonStandards?.length ?? 0) > 0) {
                    let add = false;

                    const search = 'group configuration'.toLowerCase();

                    // if we are not searching for anything, return as/is
                    if (!searchValue)
                        add = true;

                    // lets look into search
                    if (search.indexOf(searchValue) > -1)
                        add = true;

                    if (add)
                        items.push({
                            key: '-1',
                            _index: -1,
                            scope: 'Group Configuration',
                            name: 'Group Configuration',
                            search: 'group configuration',
                            total: json?.nonStandards?.length ?? 0,
                            data: {
                                id: json!.id,
                                name: json!.name,
                                entityType: 'groupConfiguration',
                                groupConfiguration: json ?? null,
                                benefit: null
                            }
                        });
                }
            }

            json?.benefits?.forEach((benefit, index) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Benefits (${benefit.bcbs_name})/${benefit.name}`;
                const search = scope.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;
                
                //make sure to only show benefits that are same category and not already on template
                searchTemplate?.relationships?.forEach(rel => {
                    if (rel.categoryID === benefit.id)
                        relationshipExists = true;
                })

                let currentBenefitCategory = props.state.data.config.cache.map_bcbs_config_category[benefit.bcbs_config_categoryid!];
                if ((add && searchTemplate?.linkedCategoryName === currentBenefitCategory.bcbs_name) && !relationshipExists)
                    items.push({
                        key: index,
                        _index: index,
                        scope: scope,
                        search: search,
                        name: scope,
                        total: benefit?.nonStandards?.length ?? 0,
                        data: {
                            id: benefit.id,
                            name: benefit.name,
                            entityType: 'benefit',
                            groupConfiguration: null,
                            benefit: benefit
                        }
                    });
            });

            // Add Accounts Receivable search
            json?.claimsARs?.forEach((ar, index) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Accounts Receivable/${ar.name}`;
                const search = scope.toLowerCase();

                if (!searchValue)
                    add = true;

                if (scope && search.indexOf(searchValue) > -1)
                    add = true;
                
                searchTemplate?.relationships?.forEach(rel => {
                    if (rel.categoryID === ar.id)
                        relationshipExists = true;
                });

                // Check if template is linked to Accounts Receivable category
                if ((add && searchTemplate?.linkedCategoryName === "Accounts Receivable") && !relationshipExists)
                    items.push({
                        key: `ar-${index}`,
                        _index: index,
                        scope: scope,
                        search: search,
                        name: scope,
                        total: ar?.nonStandards?.length ?? 0,
                        data: {
                            id: ar.id,
                            name: ar.name,
                            entityType: 'accountsReceivable',
                            groupConfiguration: null,
                            benefit: null,
                            accountsReceivable: ar
                        }
                    });
            });

            // Add Fees and Premiums search
            json?.feesAndPremiums?.forEach((fp, index) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Fees and Premiums/${fp.name}`;
                const search = scope.toLowerCase();

                if (!searchValue)
                    add = true;

                if (scope && search.indexOf(searchValue) > -1)
                    add = true;
                
                searchTemplate?.relationships?.forEach(rel => {
                    if (rel.categoryID === fp.id)
                        relationshipExists = true;
                });

                // Check if template is linked to Fees and Premiums category
                if ((add && searchTemplate?.linkedCategoryName === "Fees and Premiums") && !relationshipExists)
                    items.push({
                        key: `fp-${index}`,
                        _index: index,
                        scope: scope,
                        search: search,
                        name: scope,
                        total: fp?.nonStandards?.length ?? 0,
                        data: {
                            id: fp.id,
                            name: fp.name,
                            entityType: 'feesAndPremium',
                            groupConfiguration: null,
                            benefit: null,
                            feesAndPremium: fp
                        }
                    });
            });

            // Add Stop Loss search
            json?.stopLoss?.forEach((sl, index) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Stop Loss/${sl.name}`;
                const search = scope.toLowerCase();

                if (!searchValue)
                    add = true;

                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                searchTemplate?.relationships?.forEach(rel => {
                    if (rel.categoryID === sl.id)
                        relationshipExists = true;
                });

                const linkedCategoryName = searchTemplate?.linkedCategoryName ?? '';
                const isStopLossCategory = !!linkedCategoryName &&
                    linkedCategoryName !== 'Group Configuration' &&
                    linkedCategoryName !== 'Accounts Receivable' &&
                    linkedCategoryName !== 'Fees and Premiums' &&
                    json?.benefits?.every(b => props.state.data.config.cache.map_bcbs_config_category[b.bcbs_config_categoryid!]?.bcbs_name !== linkedCategoryName) &&
                    (json?.stopLoss?.length ?? 0) > 0;

                if (add && isStopLossCategory && !relationshipExists)
                    items.push({
                        key: `sl-${index}`,
                        _index: index,
                        scope: scope,
                        search: search,
                        name: scope,
                        total: sl?.nonStandards?.length ?? 0,
                        data: {
                            id: sl.id,
                            name: sl.name,
                            entityType: 'stopLoss',
                            groupConfiguration: null,
                            benefit: null,
                            stopLoss: sl
                        }
                    });
            });

            resolve({
                columns: [{
                    key: 'scope',
                    minWidth: 550,
                    name: 'Entity',
                    fieldName: 'scope',
                    isResizable: true,
                }],
                // {
                //     key: 'total',
                //     minWidth: 50,
                //     name: 'Count',
                //     fieldName: 'total',
                //     isResizable: true,
                // }],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,
            });
        });
    }

    function onRenderBody(): JSX.Element {
        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={props.isOpen}
                    label={'Apply Template'}
                    variant="multiple"
                    onSearch={onSearchScopes}
                    onPick={(picks) => {
                        //this is called once the thing is chosen
                        const nonStandardTemplates: FAM_INonStandardTemplate[] = [];

                        let newRelationshipList = nonStandardTemplate!.relationships!.map(rel => ({
                            ...rel,
                            sectionRefs: (rel.sectionRefs!.map(secRef => ({
                                ...secRef,
                                nonStandards: [...(secRef.nonStandards || [])] // Clone the nonstandards array
                            })) || [])
                        }));
                         //Template -> category
                        picks?.forEach(pick => {

                            if (!nonStandardTemplate) { return; }

                            const fieldMap = ArrayToLookupByPredicate(nonStandardTemplate.fields, q => q.configRef.id);

                            const linkedConfigCategory = props.state.data.config.cache.map_bcbs_config_category[nonStandardTemplate.linkedCategoryTypeId!];
                            // lets create a new nonstandard template as
                            let template: FAM_INonStandardTemplate = createNonStandardTemplate(
                                props.state.data,
                                {
                                    id: "",
                                    name: `Non-StandardTemplate - New - ${timestamp()}`,
                                    category: categoryNonStandardTemplate,
                                    linkedCategory: linkedConfigCategory ?? null,
                                    linkedCategoryName: nonStandardTemplate.linkedCategoryName ?? null,
                                    relationships: nonStandardTemplate.relationships ?? []
                                }
                            );

                            let tierChanges: ITierLevelChange[] = [];

                            // transfer matching properties 
                            template.name = nonStandardTemplate.name;
                            template.id = nonStandardTemplate.id;

                            
                            //get the category based on entityType
                            let groupConfig: FAM_IGroupConfigurationData | null = null;
                            let chosenBenefit: FAM_IBenefit | null = null;
                            let chosenAR: FAM_IAccountsReceivable | null = null;
                            let chosenFP: FAM_IFeesAndPremiums | null = null;
                            let chosenSL: FAM_IStopLoss | null = null;

                            switch (pick.entityType) {
                                case 'groupConfiguration':
                                    groupConfig = pick.groupConfiguration;
                                    break;
                                case 'benefit':
                                    chosenBenefit = pick.benefit!;
                                    break;
                                case 'accountsReceivable':
                                    chosenAR = pick.accountsReceivable!;
                                    break;
                                case 'feesAndPremium':
                                    chosenFP = pick.feesAndPremium!;
                                    break;
                                case 'stopLoss':
                                    chosenSL = pick.stopLoss!;
                                    break;
                            }

                            //we need to create all the sections 
                            //(from first relationship-- all category and sections should be the same except for NS)
                            //and place new nonstandards in them in the new relationship
                            let sourceRelationshipSections = nonStandardTemplate!.relationships![0].sectionRefs!.map(secRef => ({ ...secRef }));
                            let newRelationshipSections: FAM_ITemplateSectionRelationship[] = [];
                            
                            // Get category type name based on entity type
                            let categoryTypeName = '';
                            if (groupConfig) {
                                categoryTypeName = groupConfig.name;
                            } else if (chosenBenefit) {
                                const configCategory = props.state.data.config.cache.map_bcbs_config_category[chosenBenefit.bcbs_config_categoryid!];
                                categoryTypeName = configCategory?.bcbs_name ?? '';
                            } else if (chosenAR) {
                                categoryTypeName = 'Accounts Receivable';
                            } else if (chosenFP) {
                                categoryTypeName = 'Fees and Premiums';
                            } else if (chosenSL) {
                                categoryTypeName = nonStandardTemplate.linkedCategoryName ?? 'Stop Loss';
                            }

                            let categoryToCheck: FAM_ITemplateRelationship = {
                                categoryID: pick.id,
                                categoryType: categoryTypeName,
                                sectionRefs: sourceRelationshipSections.map(section => ({
                                    ...section,
                                    nonStandards: section.nonStandards ?? [] // Ensure nonstandards is always an array
                                }))
                            }

                            //for each section,
                            sourceRelationshipSections.forEach(section => {
                                let newNonStandardReferences: FAM_IRef[] = [];
                                section.nonStandards?.forEach(nonStandardRef => {
                                    //find matching non standard in new category - check all entity types
                                    const sourceRelCategoryId = nonStandardTemplate!.relationships![0].categoryID;
                                    let sourceCategoryNonStandard: FAM_INonStandard | undefined;
                                    
                                    // Check benefits first
                                    const sourceBenefit = props.state.data.json.benefits.find(q => q.id === sourceRelCategoryId);
                                    if (sourceBenefit) {
                                        sourceCategoryNonStandard = sourceBenefit.nonStandards.find(q => q.id === nonStandardRef.id);
                                    }
                                    // Check group configuration
                                    if (!sourceCategoryNonStandard && props.state.data.json.id === sourceRelCategoryId) {
                                        sourceCategoryNonStandard = props.state.data.json.nonStandards.find(q => q.id === nonStandardRef.id);
                                    }
                                    // Check accounts receivable
                                    if (!sourceCategoryNonStandard) {
                                        const sourceAR = props.state.data.json.claimsARs?.find(q => q.id === sourceRelCategoryId);
                                        if (sourceAR) {
                                            sourceCategoryNonStandard = sourceAR.nonStandards.find(q => q.id === nonStandardRef.id);
                                        }
                                    }
                                    // Check fees and premiums
                                    if (!sourceCategoryNonStandard) {
                                        const sourceFP = props.state.data.json.feesAndPremiums?.find(q => q.id === sourceRelCategoryId);
                                        if (sourceFP) {
                                            sourceCategoryNonStandard = sourceFP.nonStandards.find(q => q.id === nonStandardRef.id);
                                        }
                                    }
                                    // Check stop loss
                                    if (!sourceCategoryNonStandard) {
                                        const sourceSL = props.state.data.json.stopLoss?.find(q => q.id === sourceRelCategoryId);
                                        if (sourceSL) {
                                            sourceCategoryNonStandard = sourceSL.nonStandards.find(q => q.id === nonStandardRef.id);
                                        }
                                    }
                                    
                                    if (sourceCategoryNonStandard) {
                                        //find matching ones for each ref in the original category
                                        let matchingNonStandardIds = findMatchingNonStandards(sourceCategoryNonStandard, template, categoryToCheck, pick.entityType);
                                        if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
                                            matchingNonStandardIds.forEach(id => {
                                                //get the nonstandard from the target category based on entity type
                                                let categoryNonStandard: FAM_INonStandard | undefined;
                                                let targetBenefit: FAM_IBenefit | undefined;
                                                let targetAR: FAM_IAccountsReceivable | undefined;
                                                let targetFP: FAM_IFeesAndPremiums | undefined;
                                                let targetSL: FAM_IStopLoss | undefined;

                                                switch (pick.entityType) {
                                                    case 'benefit':
                                                        targetBenefit = props.state.data.json.benefits.find(q => q.id === categoryToCheck.categoryID);
                                                        categoryNonStandard = targetBenefit?.nonStandards.find(q => q.id === id);
                                                        break;
                                                    case 'groupConfiguration':
                                                        if (props.state.data.json.id === categoryToCheck.categoryID) {
                                                            categoryNonStandard = props.state.data.json.nonStandards.find(q => q.id === id);
                                                        }
                                                        break;
                                                    case 'accountsReceivable':
                                                        targetAR = props.state.data.json.claimsARs?.find(q => q.id === categoryToCheck.categoryID);
                                                        categoryNonStandard = targetAR?.nonStandards.find(q => q.id === id);
                                                        break;
                                                    case 'feesAndPremium':
                                                        targetFP = props.state.data.json.feesAndPremiums?.find(q => q.id === categoryToCheck.categoryID);
                                                        categoryNonStandard = targetFP?.nonStandards.find(q => q.id === id);
                                                        break;
                                                    case 'stopLoss':
                                                        targetSL = props.state.data.json.stopLoss?.find(q => q.id === categoryToCheck.categoryID);
                                                        categoryNonStandard = targetSL?.nonStandards.find(q => q.id === id);
                                                        break;
                                                }
                                                
                                                if (categoryNonStandard) {
                                                    //update the matched nonstandards for this category
                                                    const updatedNonStandard: FAM_INonStandard = structuredClone(categoryNonStandard);
                                                    updatedNonStandard.partOfTemplate = true;
                                                    updatedNonStandard.name = template.name;
                                                    updatedNonStandard.deactivated = sourceCategoryNonStandard!.deactivated 
                                                    //if the names are not the same, update them
                                                    let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                                    let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                                                    let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
                                                    
                                                    const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                                                    nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                                    if (nonStandardPayAccordingToBenefit) {
                                                        let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                                        const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                                        nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                                    }

                                                    newNonStandardReferences.push({ id: updatedNonStandard.id, name: updatedNonStandard.name })

                                                    dispatch(addUpdateNonStandards([{
                                                        config: props.state.data.config,
                                                        nonStandards: [updatedNonStandard],
                                                        benefit: targetBenefit,
                                                        groupConfiguration: groupConfig ?? undefined,
                                                        accountsReceivable: targetAR,
                                                        feesAndPremium: targetFP,
                                                        stopLoss: targetSL,
                                                        changeTracker: {}
                                                    }]));
                                                }
                                            })

                                        } else {
                                            let newNonStandardSection = props.state.data.config.cache.map_bcbs_config_section[section.sectionId];
                                            const newNonStandard: FAM_INonStandard = createNonStandard(
                                                props.state.data,
                                                {
                                                    section: newNonStandardSection!,
                                                    benefit: chosenBenefit ?? undefined,
                                                    groupConfiguration: groupConfig ?? undefined,
                                                    accountsReceivable: chosenAR ?? undefined,
                                                    feesAndPremium: chosenFP ?? undefined,
                                                    stopLoss: chosenSL ?? undefined,
                                                    templated: true,
                                                    name: template.name
                                                }
                                            );

                                            const fieldMap = ArrayToLookupByPredicate(nonStandardTemplate.fields, q => q.configRef.id);
                                            //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
                                            let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                            let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                                            newNonStandard.deactivated = sourceCategoryNonStandard!.deactivated 
                                            let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                                            const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                            if (nonStandardPayAccordingToBenefit) {
                                                let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                            }

                                            newNonStandardReferences.push({ id: newNonStandard.id, name: newNonStandard.name })

                                            dispatch(addUpdateNonStandards([{
                                                config: props.state.data.config,
                                                nonStandards: [newNonStandard],
                                                benefit: chosenBenefit ?? undefined,
                                                groupConfiguration: groupConfig ?? undefined,
                                                accountsReceivable: chosenAR ?? undefined,
                                                feesAndPremium: chosenFP ?? undefined,
                                                stopLoss: chosenSL ?? undefined,
                                                changeTracker: {}
                                            }]));
                                        }
                                    }
                                })

                                //create new section in relationship,
                                let newSection: FAM_ITemplateSectionRelationship =
                                {
                                    sectionId: section.sectionId,
                                    nonStandards: newNonStandardReferences
                                }

                                newRelationshipSections = [...newRelationshipSections, newSection];
                            });

                            const newCategory = {
                                categoryID: pick.id,
                                categoryType: categoryTypeName,
                                sectionRefs: newRelationshipSections.map(section => ({
                                    ...section,
                                    nonStandards: section.nonStandards ?? [] // Ensure nonstandards is always an array
                                }))
                            };
                            newRelationshipList = [...newRelationshipList, newCategory];

                            
                            template.fields.forEach(templateField => {

                                const field = fieldMap[templateField.configRef.id];

                                // did we find a matching field
                                if (field) {
                                    if (field.values.length === 0) {
                                        // nop
                                    }
                                    else if (field.values.length === 1 && templateField.values.length === 1) {

                                        const value = field.values[0].value;
                                        templateField.values[0].value = structuredClone(value);

                                    } else if (field.values.length > 1) {
                                        let predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                            return oldValue.type === newValue.type;
                                        }
                                        
                                        if (tierChanges.length > 0) {
                                            predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                                            };
                                        }

                                        templateField.values.forEach(t => {

                                            const value = field.values.find(q => predicate(q, t));

                                            if (value?.value) {
                                                t.value = structuredClone(value.value);
                                            }
                                        });
                                    }
                                }
                            });

                            const updatedTemplate = {
                                ...template,
                                relationships: newRelationshipList
                            };
                            
                            nonStandardTemplates.push(updatedTemplate);
                        });


                        if (nonStandardTemplates && nonStandardTemplates.length > 0) {
                            dispatch(
                                addUpdateNonStandardTemplates([
                                    {
                                        nonStandardTemplates: nonStandardTemplates
                                    }
                                ])
                            );
                            updateAllTemplatedNonStandardsFields(nonStandardTemplates[0]);
                            setAlertIsOpen(true);
                        }

                        //props.onDismiss?.();
                    }}
                    onCancel={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                        props.onDismiss?.();
                    }}
                />
            </Stack>
        </div>
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                // (async () => {
                //     try {
                //         dispatch(setProcessing(true));
                //         const excelExtractImport = await import("global/ExcelExtract")
                //         const { NonstandardExcelGeneration } = excelExtractImport;
                //         const result = await NonstandardExcelGeneration(props.state, dataSource)
                //         const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                //         saveAs(result.file, fileName);
                //     } catch (error) {
                //         emitCustomEvent({
                //             eventType: 'globalException',
                //             details: error
                //         });
                //     } finally {
                //         dispatch(setProcessing(false));
                //     }
                // })();
                setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.();
                setTimeout(()=>{
                    SaveAsync(
                        dispatch,
                        dataSource,
                        props.state,
                        {saveType: ESaveType.EAuto});
                },100)
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Close"
            //cancelText="Close"
        >
            <Text>
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            </Text>
        </ConfirmationDialog>;
    }

    function findMatchingNonStandards(incomingNonStandard: FAM_INonStandard, incomingTemplate: FAM_INonStandardTemplate, categoryToCheck: FAM_ITemplateRelationship, entityType: EntityType) {
        let returnIds: FAM_TGuid[] = [];
        const templatedNonStandard = structuredClone(incomingNonStandard);
        const template = structuredClone(incomingTemplate);
        const templateFieldMap = ArrayToLookupByPredicate(template.fields, q => q.configRef.id);
        let incomingNSCategoryId = categoryToCheck.categoryID;
        const nonStandardFields = ArrayToLookupByPredicateWithMap(templatedNonStandard!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const nonStandardDefinition = nonStandardFields[$att_$non_standard_$bcbs_benefitdefinition_id];
        let nonStandardDefinitionValue = "";

        if (nonStandardDefinition.field.values[0].value !== null) {
            nonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, nonStandardDefinition.field.values[0].value);
        }

        let formattedNonStandardDefinition = nonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        
        // Helper function to check nonstandards in a category
        const checkNonStandardsInCategory = (nonStandards: FAM_INonStandard[], sectionId: string) => {
            nonStandards.forEach(nonStandard => {
                if (nonStandard.sectionRef?.id === sectionId && nonStandard.deactivated !== true) {
                    const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                    const currentNonStandardDefinition = currentNonStandardFieldMap[$att_$non_standard_$bcbs_benefitdefinition_id];
                    let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                    let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                    if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                        returnIds.push(nonStandard.id);
                    }
                }
            });
        };

        //only find section refs of section ref on ns
        categoryToCheck.sectionRefs?.forEach(section => {
            if (section.sectionId === templatedNonStandard.sectionRef?.id) {
                switch (entityType) {
                    case 'groupConfiguration':
                        if (props.state.data.json.id === incomingNSCategoryId) {
                            checkNonStandardsInCategory(props.state.data.json.nonStandards, section.sectionId);
                        }
                        break;
                    case 'benefit':
                        const benefit = props.state.data.json.benefits.find(b => b.id === incomingNSCategoryId);
                        if (benefit) {
                            checkNonStandardsInCategory(benefit.nonStandards, section.sectionId);
                        }
                        break;
                    case 'accountsReceivable':
                        const ar = props.state.data.json.claimsARs?.find(a => a.id === incomingNSCategoryId);
                        if (ar) {
                            checkNonStandardsInCategory(ar.nonStandards, section.sectionId);
                        }
                        break;
                    case 'feesAndPremium':
                        const fp = props.state.data.json.feesAndPremiums?.find(f => f.id === incomingNSCategoryId);
                        if (fp) {
                            checkNonStandardsInCategory(fp.nonStandards, section.sectionId);
                        }
                        break;
                    case 'stopLoss':
                        const sl = props.state.data.json.stopLoss?.find(s => s.id === incomingNSCategoryId);
                        if (sl) {
                            checkNonStandardsInCategory(sl.nonStandards, section.sectionId);
                        }
                        break;
                }
            }
        });

        return returnIds;
    }

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM_INonStandardTemplate) {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateDefinition = templateFields[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
        const templatePayAccordingToBenefit = templateFields[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";

        
        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef => {
                sectionRef.nonStandards?.forEach(nonStandardRef => {
                    // Helper to update nonstandard fields
                    const updateNonStandardFields = (
                        nonStandard: FAM_INonStandard,
                        benefit?: FAM_IBenefit,
                        groupConfiguration?: FAM_IGroupConfigurationData,
                        accountsReceivable?: FAM_IAccountsReceivable,
                        feesAndPremium?: FAM_IFeesAndPremiums
                    ) => {
                        const updatedNonStandard: FAM_INonStandard = structuredClone(nonStandard);
                        updatedNonStandard.partOfTemplate = true;
                        updatedNonStandard.name = template.name;

                        let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                        let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                        const benefitDefinitionValue = templateDefinition.field.values[0].value;
                        nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue);

                        if (nonStandardPayAccordingToBenefit) {
                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value;
                            nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue);
                        }

                        dispatch(addUpdateNonStandards([{
                            config: props.state.data.config,
                            nonStandards: [updatedNonStandard],
                            benefit: benefit,
                            groupConfiguration: groupConfiguration,
                            accountsReceivable: accountsReceivable,
                            feesAndPremium: feesAndPremium,
                            changeTracker: {}
                        }]));
                    };

                    // Check benefits
                    let benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                    if (benefit) {
                        let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (benefitNonStandard) {
                            updateNonStandardFields(benefitNonStandard, benefit);
                            return;
                        }
                    }

                    // Check group configuration
                    if (props.state.data.json.id === categoryId) {
                        let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard) {
                            updateNonStandardFields(gcNonStandard, undefined, props.state.data.json);
                            return;
                        }
                    }

                    // Check accounts receivable
                    let ar = props.state.data.json.claimsARs?.find(a => a.id === categoryId);
                    if (ar) {
                        let arNonStandard = ar.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (arNonStandard) {
                            updateNonStandardFields(arNonStandard, undefined, undefined, ar);
                            return;
                        }
                    }

                    // Check fees and premiums
                    let fp = props.state.data.json.feesAndPremiums?.find(f => f.id === categoryId);
                    if (fp) {
                        let fpNonStandard = fp.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (fpNonStandard) {
                            updateNonStandardFields(fpNonStandard, undefined, undefined, undefined, fp);
                            return;
                        }
                    }
                })
            })
        })
    }

    return onRenderBody()

}




function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateCategoryLookup);



